import React, { useState, useEffect } from 'react';
import { useEmpresa } from '../../contexts/EmpresaContext';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { 
  Users, Plus, Search, UserPlus, Shield, Building2,
  Eye, Edit, Trash2, Key, CheckCircle, XCircle,
  RefreshCw, Mail, Lock, UserCog, Link2
} from 'lucide-react';
import { toast } from 'sonner';

// Permissões disponíveis
const PERMISSOES_EMPRESA = {
  // Visualização
  ver_marcacoes: "Visualizar marcações",
  ver_jornadas: "Visualizar jornadas",
  ver_escalas: "Visualizar escalas",
  ver_relatorios: "Visualizar relatórios",
  ver_empregados: "Visualizar empregados",
  ver_banco_horas: "Visualizar banco de horas",
  // Edição
  editar_marcacoes: "Editar/ajustar marcações",
  editar_escalas: "Criar/editar escalas",
  editar_empregados: "Cadastrar/editar empregados",
  aprovar_justificativas: "Aprovar justificativas",
  ajustar_banco_horas: "Ajustar banco de horas",
  // Administrativo
  gerar_cartao: "Gerar cartão de ponto",
  fechar_periodo: "Fechar período",
  exportar_folha: "Exportar para folha",
  configurar_empresa: "Configurações da empresa",
  gerenciar_usuarios: "Gerenciar usuários"
};

const PERFIS_ESCRITORIO = [
  { value: 'administrador_escritorio', label: 'Administrador', descricao: 'Acesso total a todas as empresas' },
  { value: 'analista_escritorio', label: 'Analista', descricao: 'Operacional - executa tarefas de DP' },
  { value: 'auxiliar_escritorio', label: 'Auxiliar', descricao: 'Acesso limitado conforme permissões' }
];

const PERFIS_NA_EMPRESA = [
  { value: 'admin', label: 'Administrador' },
  { value: 'operador', label: 'Operador' },
  { value: 'visualizador', label: 'Visualizador' }
];

const EscritorioUsuarios = () => {
  const { escritorio, empresasDisponiveis, loading: empresaLoading } = useEmpresa();
  const { api } = useAuth();
  
  const [usuarios, setUsuarios] = useState([]);
  const [vinculos, setVinculos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modais
  const [showNovoUsuario, setShowNovoUsuario] = useState(false);
  const [showEditarUsuario, setShowEditarUsuario] = useState(false);
  const [showGerenciarVinculos, setShowGerenciarVinculos] = useState(false);
  const [usuarioSelecionado, setUsuarioSelecionado] = useState(null);
  
  // Formulário novo usuário
  const [novoUsuario, setNovoUsuario] = useState({
    nome: '',
    email: '',
    senha: '',
    perfil: 'analista_escritorio'
  });
  
  // Formulário de vínculos
  const [novoVinculo, setNovoVinculo] = useState({
    empresa_id: '',
    perfil_na_empresa: 'operador',
    permissoes: []
  });

  // Formulário de edição de usuário
  const [editUsuario, setEditUsuario] = useState({
    nome: '',
    email: '',
    perfil: '',
    ativo: true
  });
  
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (escritorio?.id) {
      carregarDados();
    }
  }, [escritorio]);

  const carregarDados = async () => {
    if (!escritorio?.id) return;
    
    try {
      setLoading(true);
      
      // Buscar usuários do escritório
      const usersResponse = await api.get('/usuarios');
      const usuariosEscritorio = (usersResponse.data || []).filter(
        u => u.escritorio_id === escritorio.id
      );
      setUsuarios(usuariosEscritorio);
      
      // Buscar todos os vínculos do escritório
      const vinculosResponse = await api.get(`/usuarios-empresas?escritorio_id=${escritorio.id}`);
      setVinculos(vinculosResponse.data || []);
      
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const criarUsuario = async () => {
    if (!novoUsuario.nome || !novoUsuario.email || !novoUsuario.senha) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      setSalvando(true);
      
      await api.post('/usuarios', {
        nome: novoUsuario.nome,
        email: novoUsuario.email,
        password: novoUsuario.senha,
        perfil: novoUsuario.perfil,
        escritorio_id: escritorio.id
      });
      
      toast.success('Usuário criado com sucesso!');
      setShowNovoUsuario(false);
      setNovoUsuario({ nome: '', email: '', senha: '', perfil: 'analista_escritorio' });
      carregarDados();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar usuário');
      console.error(error);
    } finally {
      setSalvando(false);
    }
  };

  const resetarSenha = async (usuario) => {
    try {
      await api.post(`/usuarios/${usuario.id}/resetar-senha`);
      toast.success('Senha resetada! Nova senha enviada por email.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao resetar senha');
    }
  };

  const excluirUsuario = async (usuario) => {
    if (!window.confirm(`Deseja realmente excluir o usuário ${usuario.nome}?`)) return;
    
    try {
      await api.delete(`/usuarios/${usuario.id}`);
      toast.success('Usuário excluído');
      carregarDados();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir usuário');
    }
  };

  const abrirEditarUsuario = (usuario) => {
    setUsuarioSelecionado(usuario);
    setEditUsuario({
      nome: usuario.nome || '',
      email: usuario.email || '',
      perfil: usuario.perfil || 'analista_escritorio',
      ativo: usuario.ativo !== false
    });
    setShowEditarUsuario(true);
  };

  const salvarEdicaoUsuario = async () => {
    if (!usuarioSelecionado) return;

    try {
      setSalvando(true);
      await api.put(`/usuarios/${usuarioSelecionado.id}`, editUsuario);
      toast.success('Usuário atualizado com sucesso!');
      setShowEditarUsuario(false);
      setUsuarioSelecionado(null);
      carregarDados();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar usuário');
    } finally {
      setSalvando(false);
    }
  };

  const abrirGerenciarVinculos = (usuario) => {
    setUsuarioSelecionado(usuario);
    setNovoVinculo({ empresa_id: '', perfil_na_empresa: 'operador', permissoes: [] });
    setShowGerenciarVinculos(true);
  };

  const getVinculosUsuario = (usuarioId) => {
    return vinculos.filter(v => v.usuario_id === usuarioId);
  };

  const getEmpresasNaoVinculadas = (usuarioId) => {
    const vinculosUsuario = getVinculosUsuario(usuarioId);
    const empresasVinculadas = vinculosUsuario.map(v => v.empresa_id);
    return empresasDisponiveis.filter(e => !empresasVinculadas.includes(e.id));
  };

  const criarVinculo = async () => {
    if (!novoVinculo.empresa_id || !usuarioSelecionado) {
      toast.error('Selecione uma empresa');
      return;
    }

    try {
      setSalvando(true);
      
      await api.post('/usuarios-empresas', {
        usuario_id: usuarioSelecionado.id,
        empresa_id: novoVinculo.empresa_id,
        escritorio_id: escritorio.id,
        perfil_na_empresa: novoVinculo.perfil_na_empresa,
        permissoes: novoVinculo.permissoes
      });
      
      toast.success('Vínculo criado com sucesso!');
      setNovoVinculo({ empresa_id: '', perfil_na_empresa: 'operador', permissoes: [] });
      carregarDados();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar vínculo');
      console.error(error);
    } finally {
      setSalvando(false);
    }
  };

  const removerVinculo = async (vinculoId) => {
    if (!window.confirm('Deseja remover o acesso desta empresa?')) return;
    
    try {
      await api.delete(`/usuarios-empresas/${vinculoId}`);
      toast.success('Vínculo removido');
      carregarDados();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao remover vínculo');
    }
  };

  const togglePermissao = (permissao) => {
    setNovoVinculo(prev => ({
      ...prev,
      permissoes: prev.permissoes.includes(permissao)
        ? prev.permissoes.filter(p => p !== permissao)
        : [...prev.permissoes, permissao]
    }));
  };

  const selecionarTodasPermissoes = () => {
    setNovoVinculo(prev => ({
      ...prev,
      permissoes: Object.keys(PERMISSOES_EMPRESA)
    }));
  };

  const limparPermissoes = () => {
    setNovoVinculo(prev => ({ ...prev, permissoes: [] }));
  };

  const getPerfilLabel = (perfil) => {
    const p = PERFIS_ESCRITORIO.find(x => x.value === perfil);
    return p?.label || perfil;
  };

  const getPerfilColor = (perfil) => {
    switch (perfil) {
      case 'administrador_escritorio': return 'default';
      case 'analista_escritorio': return 'secondary';
      case 'auxiliar_escritorio': return 'outline';
      default: return 'outline';
    }
  };

  const usuariosFiltrados = usuarios.filter(u => {
    const termo = searchTerm.toLowerCase();
    return (
      u.nome?.toLowerCase().includes(termo) ||
      u.email?.toLowerCase().includes(termo)
    );
  });

  if (empresaLoading || loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="escritorio-usuarios">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Usuários do Escritório</h1>
          <p className="text-slate-600 mt-1">
            Gerencie os usuários e suas permissões nas empresas
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={carregarDados}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
          <Button onClick={() => setShowNovoUsuario(true)}>
            <UserPlus className="w-4 h-4 mr-2" />
            Novo Usuário
          </Button>
        </div>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{usuarios.length}</p>
                <p className="text-sm text-slate-500">Total Usuários</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {usuarios.filter(u => u.perfil === 'administrador_escritorio').length}
                </p>
                <p className="text-sm text-slate-500">Administradores</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <UserCog className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {usuarios.filter(u => u.perfil === 'analista_escritorio').length}
                </p>
                <p className="text-sm text-slate-500">Analistas</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <Link2 className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{vinculos.length}</p>
                <p className="text-sm text-slate-500">Vínculos Ativos</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Buscar por nome ou email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-usuarios"
            />
          </div>
        </CardContent>
      </Card>

      {/* Lista de Usuários */}
      <Card>
        <CardHeader>
          <CardTitle>Usuários Cadastrados</CardTitle>
          <CardDescription>
            {usuariosFiltrados.length} usuário(s) no escritório
          </CardDescription>
        </CardHeader>
        <CardContent>
          {usuariosFiltrados.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-500">
              <Users className="w-12 h-12 mb-4 opacity-50" />
              <p className="text-lg font-medium">Nenhum usuário encontrado</p>
              <p className="text-sm">Clique em "Novo Usuário" para criar</p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Usuário</TableHead>
                    <TableHead>Perfil</TableHead>
                    <TableHead className="text-center">Empresas</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {usuariosFiltrados.map((usuario) => {
                    const vinculosUsuario = getVinculosUsuario(usuario.id);
                    return (
                      <TableRow key={usuario.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                              <span className="text-lg font-medium text-slate-600">
                                {usuario.nome?.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{usuario.nome}</p>
                              <p className="text-xs text-slate-500">{usuario.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={getPerfilColor(usuario.perfil)}>
                            {getPerfilLabel(usuario.perfil)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline">
                            {vinculosUsuario.length} empresa(s)
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant={usuario.ativo !== false ? 'default' : 'secondary'}>
                            {usuario.ativo !== false ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => abrirEditarUsuario(usuario)}
                              title="Editar usuário"
                              data-testid={`btn-editar-usuario-${usuario.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => abrirGerenciarVinculos(usuario)}
                              title="Gerenciar empresas"
                              data-testid={`btn-vinculos-${usuario.id}`}
                            >
                              <Building2 className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => resetarSenha(usuario)}
                              title="Resetar senha"
                            >
                              <Key className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => excluirUsuario(usuario)}
                              title="Excluir usuário"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal Novo Usuário */}
      <Dialog open={showNovoUsuario} onOpenChange={setShowNovoUsuario}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-blue-600" />
              Novo Usuário do Escritório
            </DialogTitle>
            <DialogDescription>
              Cadastre um novo colaborador para o escritório
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Nome Completo *</Label>
              <Input
                value={novoUsuario.nome}
                onChange={(e) => setNovoUsuario({...novoUsuario, nome: e.target.value})}
                placeholder="Nome do colaborador"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={novoUsuario.email}
                onChange={(e) => setNovoUsuario({...novoUsuario, email: e.target.value})}
                placeholder="email@escritorio.com.br"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Senha Inicial *</Label>
              <Input
                type="password"
                value={novoUsuario.senha}
                onChange={(e) => setNovoUsuario({...novoUsuario, senha: e.target.value})}
                placeholder="Senha inicial"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Perfil *</Label>
              <Select 
                value={novoUsuario.perfil} 
                onValueChange={(value) => setNovoUsuario({...novoUsuario, perfil: value})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PERFIS_ESCRITORIO.map(perfil => (
                    <SelectItem key={perfil.value} value={perfil.value}>
                      <div>
                        <p className="font-medium">{perfil.label}</p>
                        <p className="text-xs text-slate-500">{perfil.descricao}</p>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
              <p className="font-medium">Próximo passo:</p>
              <p>Após criar o usuário, configure quais empresas ele poderá acessar.</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNovoUsuario(false)}>
              Cancelar
            </Button>
            <Button onClick={criarUsuario} disabled={salvando}>
              {salvando ? 'Criando...' : 'Criar Usuário'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Gerenciar Vínculos */}
      <Dialog open={showGerenciarVinculos} onOpenChange={setShowGerenciarVinculos}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              Gerenciar Acesso às Empresas
            </DialogTitle>
            <DialogDescription>
              {usuarioSelecionado?.nome} - {usuarioSelecionado?.email}
            </DialogDescription>
          </DialogHeader>
          
          {usuarioSelecionado && (
            <Tabs defaultValue="vinculos" className="mt-4">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="vinculos">Empresas Vinculadas</TabsTrigger>
                <TabsTrigger value="adicionar">Adicionar Empresa</TabsTrigger>
              </TabsList>
              
              {/* Tab Vínculos Existentes */}
              <TabsContent value="vinculos" className="space-y-4">
                {getVinculosUsuario(usuarioSelecionado.id).length === 0 ? (
                  <div className="text-center py-8 text-slate-500 border rounded-lg">
                    <Building2 className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p>Nenhuma empresa vinculada</p>
                    <p className="text-sm">Adicione empresas na aba "Adicionar Empresa"</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {getVinculosUsuario(usuarioSelecionado.id).map(vinculo => {
                      const empresa = empresasDisponiveis.find(e => e.id === vinculo.empresa_id);
                      return (
                        <div 
                          key={vinculo.id}
                          className="p-4 border rounded-lg"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                <Building2 className="w-5 h-5 text-blue-600" />
                              </div>
                              <div>
                                <p className="font-medium">
                                  {empresa?.nome_fantasia || empresa?.razao_social || 'Empresa'}
                                </p>
                                <p className="text-xs text-slate-500">
                                  Perfil: {PERFIS_NA_EMPRESA.find(p => p.value === vinculo.perfil_na_empresa)?.label || vinculo.perfil_na_empresa}
                                </p>
                              </div>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => removerVinculo(vinculo.id)}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Remover
                            </Button>
                          </div>
                          
                          {vinculo.permissoes?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {vinculo.permissoes.slice(0, 5).map(p => (
                                <Badge key={p} variant="outline" className="text-xs">
                                  {PERMISSOES_EMPRESA[p] || p}
                                </Badge>
                              ))}
                              {vinculo.permissoes.length > 5 && (
                                <Badge variant="outline" className="text-xs">
                                  +{vinculo.permissoes.length - 5} mais
                                </Badge>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </TabsContent>
              
              {/* Tab Adicionar Empresa */}
              <TabsContent value="adicionar" className="space-y-4">
                {getEmpresasNaoVinculadas(usuarioSelecionado.id).length === 0 ? (
                  <div className="text-center py-8 text-slate-500 border rounded-lg">
                    <CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-500" />
                    <p>Todas as empresas já estão vinculadas</p>
                  </div>
                ) : (
                  <>
                    <div>
                      <Label>Selecione a Empresa *</Label>
                      <Select 
                        value={novoVinculo.empresa_id} 
                        onValueChange={(value) => setNovoVinculo({...novoVinculo, empresa_id: value})}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Escolha uma empresa..." />
                        </SelectTrigger>
                        <SelectContent>
                          {getEmpresasNaoVinculadas(usuarioSelecionado.id).map(empresa => (
                            <SelectItem key={empresa.id} value={empresa.id}>
                              {empresa.nome_fantasia || empresa.razao_social}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Perfil na Empresa</Label>
                      <Select 
                        value={novoVinculo.perfil_na_empresa} 
                        onValueChange={(value) => setNovoVinculo({...novoVinculo, perfil_na_empresa: value})}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PERFIS_NA_EMPRESA.map(perfil => (
                            <SelectItem key={perfil.value} value={perfil.value}>
                              {perfil.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {usuarioSelecionado.perfil === 'auxiliar_escritorio' && (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <Label>Permissões Específicas</Label>
                          <div className="flex gap-2">
                            <Button 
                              type="button" 
                              variant="ghost" 
                              size="sm"
                              onClick={selecionarTodasPermissoes}
                            >
                              Todas
                            </Button>
                            <Button 
                              type="button" 
                              variant="ghost" 
                              size="sm"
                              onClick={limparPermissoes}
                            >
                              Nenhuma
                            </Button>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-2 max-h-[200px] overflow-y-auto border rounded-lg p-3">
                          {Object.entries(PERMISSOES_EMPRESA).map(([key, label]) => (
                            <label 
                              key={key}
                              className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 p-1 rounded"
                            >
                              <Checkbox
                                checked={novoVinculo.permissoes.includes(key)}
                                onCheckedChange={() => togglePermissao(key)}
                              />
                              <span className="truncate">{label}</span>
                            </label>
                          ))}
                        </div>
                        
                        <p className="text-xs text-slate-500 mt-2">
                          * Auxiliares precisam de permissões específicas. Admins e Analistas têm acesso total.
                        </p>
                      </div>
                    )}

                    {usuarioSelecionado.perfil !== 'auxiliar_escritorio' && (
                      <div className="p-3 bg-green-50 rounded-lg text-sm text-green-700">
                        <CheckCircle className="w-4 h-4 inline mr-1" />
                        {usuarioSelecionado.perfil === 'administrador_escritorio' 
                          ? 'Administradores têm acesso total a todas as funcionalidades.'
                          : 'Analistas têm acesso operacional completo às empresas vinculadas.'}
                      </div>
                    )}

                    <Button 
                      onClick={criarVinculo} 
                      disabled={!novoVinculo.empresa_id || salvando}
                      className="w-full"
                    >
                      {salvando ? 'Vinculando...' : 'Vincular Empresa'}
                    </Button>
                  </>
                )}
              </TabsContent>
            </Tabs>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGerenciarVinculos(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Editar Usuário */}
      <Dialog open={showEditarUsuario} onOpenChange={setShowEditarUsuario}>
        <DialogContent className="max-w-lg" data-testid="modal-editar-usuario">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-600" />
              Editar Usuário
            </DialogTitle>
            <DialogDescription>
              Atualize os dados do usuário do escritório
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome *</Label>
              <Input
                data-testid="edit-nome-usuario"
                value={editUsuario.nome}
                onChange={(e) => setEditUsuario({...editUsuario, nome: e.target.value})}
                placeholder="Nome completo"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                data-testid="edit-email-usuario"
                type="email"
                value={editUsuario.email}
                onChange={(e) => setEditUsuario({...editUsuario, email: e.target.value})}
                placeholder="email@escritorio.com.br"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Perfil *</Label>
              <Select 
                value={editUsuario.perfil} 
                onValueChange={(value) => setEditUsuario({...editUsuario, perfil: value})}
              >
                <SelectTrigger data-testid="edit-perfil-usuario">
                  <SelectValue placeholder="Selecione o perfil" />
                </SelectTrigger>
                <SelectContent>
                  {PERFIS_ESCRITORIO.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      <div className="flex flex-col">
                        <span>{p.label}</span>
                        <span className="text-xs text-slate-500">{p.descricao}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox 
                id="edit-ativo"
                data-testid="edit-ativo-usuario"
                checked={editUsuario.ativo}
                onCheckedChange={(checked) => setEditUsuario({...editUsuario, ativo: checked})}
              />
              <Label htmlFor="edit-ativo">Usuário ativo</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditarUsuario(false)} disabled={salvando}>
              Cancelar
            </Button>
            <Button onClick={salvarEdicaoUsuario} disabled={salvando} data-testid="btn-salvar-usuario">
              {salvando ? 'Salvando...' : 'Salvar Alterações'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EscritorioUsuarios;
